import { useCallback, useState } from 'react';

import { downloadBlob } from '@src/common';
import { store, getClient, bearerAuth } from '@src/core/store';
import type { VoucherDownloadTarget } from '@src/domains/vouchers';

interface LoadingByKey {
  [key: string]: boolean;
}

const FILE_PREFIX_BY_KIND: Record<VoucherDownloadTarget['kind'], string> = {
  voucher: 'voucher',
  reservation: 'vouchers-reservation',
  order: 'vouchers-order',
  sale: 'vouchers-sale',
};

const fileNameFor = (target: VoucherDownloadTarget) => `${FILE_PREFIX_BY_KIND[target.kind]}-${target.id}.pdf`;

export function useVoucherDownload() {
  const [loading, setLoading] = useState<LoadingByKey>({});

  const download = useCallback(async (target: VoucherDownloadTarget) => {
    const state = store.getState();
    const {
      auth: { buyerToken },
    } = state;

    if (buyerToken == null) {
      return;
    }

    setLoading((current) => ({ ...current, [target.id]: true }));

    try {
      const options = { ...bearerAuth(buyerToken), responseType: 'blob' as const };
      const vouchersApi = getClient(state).vouchers;

      let blob;

      switch (target.kind) {
        case 'voucher':
          ({ data: blob } = await vouchersApi.getVoucherPdf(target.id, options));
          break;
        case 'reservation':
          ({ data: blob } = await vouchersApi.getReservationVouchersPdf(target.id, options));
          break;
        case 'order':
          ({ data: blob } = await vouchersApi.getOrderVouchersPdf(target.id, options));
          break;
        case 'sale':
          ({ data: blob } = await vouchersApi.getSaleVouchersPdf(target.id, options));
          break;
        default: {
          // Compiler-enforced: a new target kind has to pick an endpoint here.
          const exhaustive: never = target;

          return exhaustive;
        }
      }

      if (blob) {
        downloadBlob(blob, fileNameFor(target));
      }
    } finally {
      setLoading((current) => ({ ...current, [target.id]: false }));
    }
  }, []);

  const isDownloading = useCallback((key: string) => !!loading[key], [loading]);

  return { download, isDownloading };
}
